
// client/src/components/tenant/TenantUsers.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { listTenantUsers, setUserActive } from "../../services/tenantUsersApi";
import { getSessionLocal } from "../../services/sessionLocal";
import { permissionsFor } from "../../services/rolePermissions";
import AppFooter from "../AppFooter";
import { FaHome, FaUserPlus, FaLock } from "react-icons/fa";

export default function TenantUsers() {
  const { tenant } = useParams();
  const tenantNum = Number(tenant) || null;
  const navigate = useNavigate();
  const session = getSessionLocal();
  const perms = permissionsFor(session.role, 'tenantUsers');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState({}); // { [userId]: true }

  const refresh = async () => {
    if (!tenantNum) return;
    setLoading(true);
    setErr(null);
    try {
      const data = await listTenantUsers(tenantNum);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleDashboard = () => {
    navigate(`/${tenant}/dashboard`);
  };

  useEffect(() => {
    setRows([]);
    if (tenantNum) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantNum]);

  const toggleActive = async (e, u) => {
    e.stopPropagation(); // don’t navigate when clicking the button
    const next = u.active ? 0 : 1;

    setSaving((s) => ({ ...s, [u.id]: true }));
    // optimistic UI update
    setRows((prev) =>
      prev.map((r) => (r.id === u.id ? { ...r, active: next } : r))
    );

    try {
      await setUserActive(tenantNum, u.id, next);
    } catch (e2) {
      // revert on error
      setRows((prev) =>
        prev.map((r) => (r.id === u.id ? { ...r, active: u.active } : r))
      );
      alert(e2.message || "Failed to update user");
    } finally {
      setSaving((s) => {
        const n = { ...s };
        delete n[u.id];
        return n;
      });
    }
  };

  if (!tenantNum) {
    return (
      <div className="container py-3">
        <div className="alert alert-warning mt-3">
          Missing tenant id. Route should be <code>/:tenant/tenantUsers</code>.
        </div>
      </div>
    );
  }

  if (!perms.view) {
    return (
      <div className="container py-3">
        <div className="alert alert-danger mt-3 flex items-center" style={{ gap: "10px" }}>
          <FaLock /> You do not have permission to view tenant users.
        </div>
        <AppFooter buttons={[{ icon: <FaHome />, label: "Home", onClick: () => navigate(`/${tenant}/dashboard`) }]} />
      </div>
    );
  }

  // Sort by last name, first name
  const sortedRows = [...rows].sort((a, b) => {
    const aLast = (a?.lastname || "").toLowerCase();
    const bLast = (b?.lastname || "").toLowerCase();
    if (aLast !== bLast) return aLast.localeCompare(bLast);
    const aFirst = (a?.firstname || "").toLowerCase();
    const bFirst = (b?.firstname || "").toLowerCase();
    return aFirst.localeCompare(bFirst);
  });

  const hasUsers = sortedRows.length > 0;

  return (
    <div className="container py-3">
      {/* Desktop inline action bar (replaces footer on desktop) */}
      <div className="desktop-action-bar">
        <button type="button" onClick={handleDashboard}>
          <FaHome /> Home
        </button>
        {perms.add && (
          <button type="button" className="btn-primary-action" onClick={() => navigate(`/${tenant}/userAdd`)}>
            <FaUserPlus /> Add User
          </button>
        )}
      </div>

      {/* Header – same style as ScheduledPostsList */}
      <div className="flex justify-between items-center mb-3">
        <div>
          <h5 className="mb-0">Users</h5>
        </div>
        {/* <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={loading}
          onClick={refresh}
        >
          <FaSync className={loading ? "fa-spin me-1" : "me-1"} />
          Refresh
        </button> */}
      </div>

      {/* Error banner */}
      {!!err && (
        <div className="alert alert-danger" role="alert">
          <strong>Error:</strong> {err}
        </div>
      )}

      {/* Loading state */}
      {loading && !hasUsers && (
        <div className="text-center text-gray-400 py-4">
          Loading users…
        </div>
      )}

      {/* Empty state */}
      {!loading && !hasUsers && !err && (
        <div className="text-center text-gray-400 py-4">
          No users found for this tenant.
        </div>
      )}

      {/* User list – card style like ScheduledPostsList */}
      {hasUsers && (
        <div className="list-group mb-3" style={{ background: "#3C3C3C", borderRadius: 8, padding: 6 }}>
          {sortedRows.map((u) => {
            const name =
              u.displayName ||
              [u.firstname, u.lastname].filter(Boolean).join(" ") ||
              u.username ||
              "(no name)";
            const active = !!u.active;

            return (
              <div
                key={u.id}
                className="list-group-item mb-2 rounded shadow-sm"
                style={{ cursor: perms.update ? "pointer" : "default" }}
                onClick={() => perms.update && navigate(`/${tenant}/${u.id}/user`)}
              >
                <div className="flex justify-between items-start">
                  {/* Left side – main info */}
                  <div style={{ textAlign: "left", minWidth: 0, flex: "1 1 0%" }}>
                    <div className="font-semibold truncate" style={{ color: "#212529" }}>{name}</div>
                    <div className="small truncate" style={{ color: "#6c757d" }}>
                      {u.username || "No username"}
                    </div>
                    {u.email && (
                      <div className="small truncate" style={{ color: "#6c757d" }}>{u.email}</div>
                    )}
                    {u.role && (
                      <div className="small truncate" style={{ color: "#6c757d" }}>{u.role}</div>
                    )}
                  </div>

                  {/* Right side – status + toggle */}
                  <div
                    className="flex flex-col items-end"
                    style={{ gap: "0.25rem", flexShrink: 0, marginLeft: "0.5rem" }}
                  >
                    <span
                      className={`badge ${
                        active ? "bg-success" : "bg-secondary"
                      }`}
                    >
                      {active ? "Active" : "Inactive"}
                    </span>
                    {perms.update && (
                      <button
                        type="button"
                        className={`btn btn-sm ${
                          active ? "btn-warning" : "btn-primary"
                        }`}
                        disabled={!!saving[u.id]}
                        onClick={(e) => toggleActive(e, u)}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {saving[u.id]
                          ? "Saving…"
                          : active
                          ? "Set Inactive"
                          : "Set Active"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer – same pattern as other screens */}
      <AppFooter
        buttons={[
          {
            icon: <FaHome />,
            label: "Home",
            onClick: handleDashboard,
          },
          ...(perms.add ? [{
            icon: <FaUserPlus />,
            label: "Add User",
            onClick: () => navigate(`/${tenant}/userAdd`),
          }] : []),
        ]}
      />
    </div>
  );
}
